import { Link } from "react-router";
import { CompanyLogo } from "@/pages/company/CompanyLogo";
import type { CompanyLogoValue } from "@/pages/company/company-logo";

export interface CompanyResultRowProps {
  id: string;
  /** The company's name as it should read. */
  name: string;
  /** Why this company is here — the expertise label that carried it, or nothing. */
  sub?: string | null;
  logo?: CompanyLogoValue | null;
  /**
   * Whether this company is one the reader may open. FALSE renders the same row WITHOUT the link.
   *
   * The assistant's company search reaches further than the reader does, so a row is not a link
   * until `companyIdentities` has answered for its id. Linking first and finding out later would
   * land the reader on "This company does not exist or is not visible to you" — a correct message
   * that reads as a broken answer.
   */
  openable: boolean;
}

/**
 * One company in an assistant answer: its logo, its name, and why it is there.
 *
 * The company counterpart of `PersonResultRow`, which it mirrors down to the hover and focus rings.
 * Not a variant of it: the two differ in exactly the two places that matter — `CompanyLogo` rather
 * than `Avatar` (a different fallback, owned by the company page) and `/c/:id` rather than `/p/:id`
 * — and a shared row carrying both would be a prop for each.
 */
export function CompanyResultRow({ id, name, sub, logo, openable }: CompanyResultRowProps) {
  const baseClass = "flex items-center gap-2 bg-page box-flat p-2";
  const inner = (
    <>
      {/* THE SAME COMPONENT THE COMPANY PAGE AND /c DRAW, at its compact rung: the monogram fallback
          is a decision that component owns, and a second copy here is a second place for it to drift. */}
      <CompanyLogo logo={logo} name={name} size="compact" />
      {/* Name left, label right, ONE LINE always — which is where the row's height comes from: a
          logo and a line, not a logo and a stack. Each side yields rather than pushing:

          - Both carry `min-w-0` + `truncate`, so the row's min-content width stays near zero. The
            answer bubble is a flex item sized by its content (`max-w-[85%]`, no width), so a row
            that insisted on a width pushed straight through its right edge.
          - The label is capped at 45% and never shrinks below what fits there, so a long one
            ("Photovoltaik / Solarenergie") takes its share and truncates rather than crowding the
            name out or dropping to a second line.
          - No container query: `container-type: inline-size` stops children counting towards
            intrinsic width, which collapsed the whole bubble to the prose beside the rows. */}
      <span className="flex min-w-0 flex-1 items-baseline gap-2">
        <span
          className={`min-w-0 flex-1 truncate text-smycles-body text-ink${openable ? " transition group-hover:text-ink-accent" : ""}`}
        >
          {name}
        </span>
        {sub ? (
          <span className="max-w-[45%] shrink-0 truncate text-smycles-meta text-ink-muted">{sub}</span>
        ) : null}
      </span>
    </>
  );

  if (!openable) return <div className={baseClass}>{inner}</div>;
  return (
    <Link
      to={`/c/${id}`}
      className={`${baseClass} group cursor-pointer transition hover:ring-accent-decoration/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui`}
    >
      {inner}
    </Link>
  );
}
